import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { ThumbWithFallback } from "./ThumbWithFallback";
import { BigButton } from "../design/BigButton";
import { DEFAULT_OBJECT_TOKEN_REWARD } from "../constants/gameplay";
import { polygonBbox, circleBbox } from "../gameplay/hitboxMath";

/**
 * LevelEditor — interactive canvas + form panel with shape-based hitboxes.
 *
 * Coordinates are stored as percentages relative to the scene background,
 * which matches how the child gameplay engine consumes them.
 *
 * Iteration 20 polish adds:
 *   • Distinct per-object polygon colors + dim inactive overlays
 *   • Undo (Ctrl+Z) up to 20 vertex operations
 *   • Arrow-key nudge on selected vertex (0.5% / Shift = 0.1%)
 *   • Shift-drag snaps to 1% grid
 *   • Left-click on a polygon EDGE inserts a new vertex at that point
 *   • Right-click a vertex deletes it (existing behaviour)
 *   • "Reset to seeded" per polygon + "Auto-fit" (OpenCV) + "Test tap" mode
 *   • Object thumbnails in sidebar list
 *   • Sidebar vertex table shows px + % side by side
 *   • Ctrl+S / Cmd+S save shortcut
 *   • Preview toggle hides vertex handles
 */
const PALETTE = [
  { fill: "rgba(239, 68, 68, 0.30)",   stroke: "#ef4444" },
  { fill: "rgba(59, 130, 246, 0.30)",  stroke: "#3b82f6" },
  { fill: "rgba(34, 197, 94, 0.30)",   stroke: "#22c55e" },
  { fill: "rgba(245, 158, 11, 0.30)",  stroke: "#f59e0b" },
  { fill: "rgba(168, 85, 247, 0.30)",  stroke: "#a855f7" },
  { fill: "rgba(20, 184, 166, 0.30)",  stroke: "#14b8a6" },
  { fill: "rgba(236, 72, 153, 0.30)",  stroke: "#ec4899" },
  { fill: "rgba(132, 204, 22, 0.30)",  stroke: "#84cc16" },
  { fill: "rgba(249, 115, 22, 0.30)",  stroke: "#f97316" },
  { fill: "rgba(6, 182, 212, 0.30)",   stroke: "#06b6d4" },
];
const paletteFor = (i) => PALETTE[i % PALETTE.length];
const UNDO_DEPTH = 20;
const NUDGE_COARSE = 0.5;   // arrow keys
const NUDGE_FINE = 0.1;     // shift+arrow

export function LevelEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [level, setLevel] = useState(null);
  const [objects, setObjects] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [selectedVertex, setSelectedVertex] = useState(-1);
  const [learningObjects, setLearningObjects] = useState([]);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [testTapMode, setTestTapMode] = useState(false);
  const [testTapResult, setTestTapResult] = useState(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  // Undo history: array of snapshots of `objects`. Push on every mutation.
  const undoRef = useRef([]);
  // Reset-baseline: the objects as they were on load (never mutated after).
  const baselineRef = useRef([]);

  const pushUndo = useCallback((snapshot) => {
    undoRef.current.push(JSON.parse(JSON.stringify(snapshot)));
    if (undoRef.current.length > UNDO_DEPTH) undoRef.current.shift();
  }, []);

  async function load() {
    try {
      const [{ data }, lo] = await Promise.all([
        api.get(`/admin/levels/${id}/objects`),
        api.get("/admin/learning-objects"),
      ]);
      setLevel(data.level);
      const normalized = (data.objects || []).map((o) => ({
        id: o.id,
        learningObjectId: o.learningObjectId,
        learningObject: o.learningObject,
        xPercent: o.xPercent, yPercent: o.yPercent,
        widthPercent: o.widthPercent, heightPercent: o.heightPercent,
        hitboxPaddingPercent: o.hitboxPaddingPercent ?? 0,
        hintText: o.hintText || "",
        hintRegionX: o.hintRegionX || 0, hintRegionY: o.hintRegionY || 0,
        hintRegionWidth: o.hintRegionWidth || 0, hintRegionHeight: o.hintRegionHeight || 0,
        isRequired: !!o.isRequired, isBonusObject: !!o.isBonusObject,
        tokenReward: o.tokenReward ?? DEFAULT_OBJECT_TOKEN_REWARD,
        difficulty: o.difficulty || "easy",
        sortOrder: o.sortOrder ?? 0,
        hitboxShape: o.hitboxShape || "polygon",
        hitboxPolygon: Array.isArray(o.hitboxPolygon) ? o.hitboxPolygon.map((p) => [p[0], p[1]]) : [],
        hitboxCircle: o.hitboxCircle ? { ...o.hitboxCircle } : null,
        missionThumbnailUrl: o.missionThumbnailUrl || "",
      }));
      setObjects(normalized);
      baselineRef.current = JSON.parse(JSON.stringify(normalized));
      undoRef.current = [];
      setLearningObjects(lo.data.items || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }
  useEffect(() => { load(); }, [id]);

  const selected = objects[selectedIdx];
  const validation = useMemo(() => validate(level, objects), [level, objects]);

  // Wrap every state mutation so undo history captures the PRE-change snapshot.
  const mutate = useCallback((fn) => {
    setObjects((prev) => {
      pushUndo(prev);
      return fn(prev);
    });
  }, [pushUndo]);

  function patchObject(idx, patch) {
    mutate((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  }

  function switchShape(idx, nextShape) {
    mutate((prev) => prev.map((o, i) => {
      if (i !== idx) return o;
      if (nextShape === "circle") {
        const cx = o.xPercent + o.widthPercent / 2;
        const cy = o.yPercent + o.heightPercent / 2;
        const radius = Math.max(1, Math.min(o.widthPercent, o.heightPercent) / 2);
        return { ...o, hitboxShape: "circle", hitboxCircle: o.hitboxCircle || { cx, cy, radius } };
      }
      if (nextShape === "polygon") {
        const x1 = o.xPercent, y1 = o.yPercent;
        const x2 = o.xPercent + o.widthPercent, y2 = o.yPercent + o.heightPercent;
        return {
          ...o,
          hitboxShape: "polygon",
          hitboxPolygon: (o.hitboxPolygon && o.hitboxPolygon.length >= 3)
            ? o.hitboxPolygon
            : [[x1, y1], [x2, y1], [x2, y2], [x1, y2]],
        };
      }
      return { ...o, hitboxShape: "rectangle" };
    }));
    setSelectedVertex(-1);
  }

  // ---------- Bounding rect drag/resize ----------
  const onPointerDown = useCallback((e, idx, mode) => {
    if (!canvasRef.current || previewMode || testTapMode) return;
    e.preventDefault(); e.stopPropagation();
    setSelectedIdx(idx); setSelectedVertex(-1);
    pushUndo(objects);
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      idx, mode, rect,
      startX: e.clientX, startY: e.clientY,
      original: { ...objects[idx], hitboxCircle: objects[idx].hitboxCircle ? { ...objects[idx].hitboxCircle } : null },
    };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const shift = ev.shiftKey;
      const dxPct = ((ev.clientX - d.startX) / d.rect.width) * 100;
      const dyPct = ((ev.clientY - d.startY) / d.rect.height) * 100;
      setObjects((prev) => prev.map((o, i) => {
        if (i !== d.idx) return o;
        if (d.mode === "move") {
          let newX = clamp(d.original.xPercent + dxPct, 0, 100 - o.widthPercent);
          let newY = clamp(d.original.yPercent + dyPct, 0, 100 - o.heightPercent);
          if (shift) { newX = Math.round(newX); newY = Math.round(newY); }
          const sx = newX - d.original.xPercent; const sy = newY - d.original.yPercent;
          return {
            ...o, xPercent: newX, yPercent: newY,
            hitboxCircle: d.original.hitboxCircle
              ? { ...d.original.hitboxCircle, cx: d.original.hitboxCircle.cx + sx, cy: d.original.hitboxCircle.cy + sy }
              : o.hitboxCircle,
            hitboxPolygon: o.hitboxShape === "polygon"
              ? d.original.hitboxPolygon.map(([x, y]) => [x + sx, y + sy])
              : o.hitboxPolygon,
          };
        }
        let w = clamp(d.original.widthPercent + dxPct, 2, 100 - o.xPercent);
        let h = clamp(d.original.heightPercent + dyPct, 2, 100 - o.yPercent);
        if (shift) { w = Math.round(w); h = Math.round(h); }
        return { ...o, widthPercent: w, heightPercent: h };
      }));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [objects, previewMode, testTapMode, pushUndo]);

  // ---------- Circle center + radius drag ----------
  const onCircleDown = useCallback((e, idx, mode) => {
    if (!canvasRef.current || previewMode || testTapMode) return;
    e.preventDefault(); e.stopPropagation();
    setSelectedIdx(idx);
    pushUndo(objects);
    const rect = canvasRef.current.getBoundingClientRect();
    const original = objects[idx].hitboxCircle ? { ...objects[idx].hitboxCircle } : null;
    if (!original) return;
    dragRef.current = { idx, mode, rect, startX: e.clientX, startY: e.clientY, original };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const shift = ev.shiftKey;
      const dxPct = ((ev.clientX - d.startX) / d.rect.width) * 100;
      const dyPct = ((ev.clientY - d.startY) / d.rect.height) * 100;
      setObjects((prev) => prev.map((o, i) => {
        if (i !== d.idx) return o;
        if (d.mode === "circle-move") {
          let cx = clamp(d.original.cx + dxPct, 0, 100);
          let cy = clamp(d.original.cy + dyPct, 0, 100);
          if (shift) { cx = Math.round(cx); cy = Math.round(cy); }
          const nextCircle = { ...d.original, cx, cy };
          return { ...o, hitboxCircle: nextCircle, ...(circleBbox(nextCircle) || {}) };
        }
        let r = clamp(d.original.radius + dxPct, 1, 50);
        if (shift) r = Math.round(r);
        const nextCircle = { ...d.original, radius: r };
        return { ...o, hitboxCircle: nextCircle, ...(circleBbox(nextCircle) || {}) };
      }));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [objects, previewMode, testTapMode, pushUndo]);

  // ---------- Polygon vertex drag ----------
  const onVertexDown = useCallback((e, idx, vIdx) => {
    if (!canvasRef.current || previewMode || testTapMode) return;
    e.preventDefault(); e.stopPropagation();
    setSelectedIdx(idx); setSelectedVertex(vIdx);
    pushUndo(objects);
    const rect = canvasRef.current.getBoundingClientRect();
    const original = [...objects[idx].hitboxPolygon.map((p) => [p[0], p[1]])];
    dragRef.current = { idx, vIdx, mode: "vertex", rect, startX: e.clientX, startY: e.clientY, original };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const shift = ev.shiftKey;
      const dxPct = ((ev.clientX - d.startX) / d.rect.width) * 100;
      const dyPct = ((ev.clientY - d.startY) / d.rect.height) * 100;
      setObjects((prev) => prev.map((o, i) => {
        if (i !== d.idx) return o;
        const [ox, oy] = d.original[d.vIdx];
        let nx = clamp(ox + dxPct, 0, 100);
        let ny = clamp(oy + dyPct, 0, 100);
        if (shift) { nx = Math.round(nx); ny = Math.round(ny); }
        const next = o.hitboxPolygon.map((p, k) => (k === d.vIdx ? [nx, ny] : p));
        return { ...o, hitboxPolygon: next, ...(polygonBbox(next) || {}) };
      }));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [objects, previewMode, testTapMode, pushUndo]);

  function deleteVertex(idx, vIdx) {
    mutate((prev) => prev.map((o, i) => {
      if (i !== idx) return o;
      if (o.hitboxPolygon.length <= 3) return o;
      const nextPoly = o.hitboxPolygon.filter((_, k) => k !== vIdx);
      return { ...o, hitboxPolygon: nextPoly, ...(polygonBbox(nextPoly) || {}) };
    }));
    setSelectedVertex(-1);
  }

  function simplifyPolygon(idx) {
    mutate((prev) => prev.map((o, i) => {
      if (i !== idx || o.hitboxShape !== "polygon") return o;
      const nextPoly = removeNearDuplicates(o.hitboxPolygon, 1.0);
      return { ...o, hitboxPolygon: nextPoly, ...(polygonBbox(nextPoly) || {}) };
    }));
  }

  function resetToSeeded(idx) {
    const base = baselineRef.current[idx];
    if (!base) return;
    mutate((prev) => prev.map((o, i) => (i === idx ? JSON.parse(JSON.stringify(base)) : o)));
    setSelectedVertex(-1);
    setMsg(`Reset "${base.learningObject?.displayName}" to its seeded polygon.`);
  }

  async function autoFit(idx) {
    if (!level?.sceneImageUrl) { setErr("Scene URL not set."); return; }
    const o = objects[idx];
    setMsg(null); setErr(null);
    try {
      const { data } = await api.post(`/admin/levels/${id}/auto-fit-polygon`, {
        sceneImageUrl: level.sceneImageUrl,
        xPercent: o.xPercent, yPercent: o.yPercent,
        widthPercent: o.widthPercent, heightPercent: o.heightPercent,
      });
      mutate((prev) => prev.map((oo, i) => (i === idx
        ? { ...oo, hitboxShape: "polygon", hitboxPolygon: data.polygon, hitboxCircle: null, ...(polygonBbox(data.polygon) || {}) }
        : oo)));
      setMsg(`Auto-fit "${o.learningObject?.displayName}" via ${data.source} (${data.polygon.length} verts).`);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  // ---------- Canvas click routing ----------
  // Priority: test-tap mode > edge-insert (polygon selected, click near edge) > deselect
  const onCanvasClick = useCallback((e) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    if (xPct < 0 || yPct < 0 || xPct > 100 || yPct > 100) return;

    if (testTapMode) {
      // Find which polygon contains the tap (bbox for rectangles/circles).
      const hits = objects.map((o, i) => ({ i, o, hit: pointInHitbox(xPct, yPct, o) }))
        .filter((x) => x.hit);
      setTestTapResult({
        xPct, yPct,
        clientX: e.clientX - rect.left,
        clientY: e.clientY - rect.top,
        names: hits.map(({ o }) => o.learningObject?.canonicalName || "?"),
      });
      return;
    }

    // Polygon edge-click → insert vertex if we're within 2% of an edge of
    // the SELECTED polygon.
    if (selected && selected.hitboxShape === "polygon") {
      const poly = selected.hitboxPolygon;
      let best = { idx: -1, dist: Infinity, pt: null };
      for (let i = 0; i < poly.length; i++) {
        const a = poly[i], b = poly[(i + 1) % poly.length];
        const { dist, pt } = distPointToSegment(xPct, yPct, a[0], a[1], b[0], b[1]);
        if (dist < best.dist) best = { idx: i, dist, pt };
      }
      if (best.dist < 2.0) {  // within 2% of an edge → insert
        mutate((prev) => prev.map((o, i) => {
          if (i !== selectedIdx) return o;
          const p = [round1(best.pt[0]), round1(best.pt[1])];
          const next = [...o.hitboxPolygon];
          next.splice(best.idx + 1, 0, p);
          return { ...o, hitboxPolygon: next, ...(polygonBbox(next) || {}) };
        }));
        setSelectedVertex(best.idx + 1);
        return;
      }
    }
    // Otherwise: click on empty area deselects (or if the click landed on
    // another object, its own onPointerDown already handled selection).
    setSelectedIdx(-1);
    setSelectedVertex(-1);
  }, [testTapMode, objects, selected, selectedIdx, mutate]);

  function addObject() {
    if (learningObjects.length === 0) return;
    const lo = learningObjects[0];
    const x1 = 10, y1 = 10, x2 = 22, y2 = 25;
    mutate((prev) => [
      ...prev,
      {
        learningObjectId: lo.id, learningObject: lo,
        xPercent: x1, yPercent: y1, widthPercent: x2 - x1, heightPercent: y2 - y1,
        hitboxPaddingPercent: 0, hintText: "",
        hintRegionX: 0, hintRegionY: 0, hintRegionWidth: 0, hintRegionHeight: 0,
        isRequired: true, isBonusObject: false, tokenReward: DEFAULT_OBJECT_TOKEN_REWARD,
        difficulty: level?.difficulty || "easy", sortOrder: prev.length + 1,
        hitboxShape: "polygon",
        hitboxPolygon: [[x1, y1], [x2, y1], [x2, y2], [x1, y2]],
        hitboxCircle: null,
      },
    ]);
    setSelectedIdx(objects.length);
    setSelectedVertex(-1);
  }

  function removeSelected() {
    if (!selected) return;
    mutate((prev) => prev.filter((_, i) => i !== selectedIdx));
    setSelectedIdx(0);
    setSelectedVertex(-1);
  }

  async function save() {
    setSaving(true); setErr(null); setMsg(null);
    try {
      const payload = objects.map((o, i) => ({ ...o, sortOrder: i + 1 }));
      const { data } = await api.put(`/admin/levels/${id}/objects`, {
        objects: payload.map((p) => ({
          id: p.id, learningObjectId: p.learningObjectId,
          xPercent: p.xPercent, yPercent: p.yPercent,
          widthPercent: p.widthPercent, heightPercent: p.heightPercent,
          hitboxPaddingPercent: p.hitboxPaddingPercent,
          hintText: p.hintText,
          hintRegionX: p.hintRegionX, hintRegionY: p.hintRegionY,
          hintRegionWidth: p.hintRegionWidth, hintRegionHeight: p.hintRegionHeight,
          isRequired: p.isRequired, isBonusObject: p.isBonusObject,
          tokenReward: p.tokenReward, difficulty: p.difficulty,
          sortOrder: p.sortOrder,
          hitboxShape: p.hitboxShape || "polygon",
          hitboxPolygon: p.hitboxShape === "polygon" ? p.hitboxPolygon : [],
          hitboxCircle: p.hitboxShape === "circle" ? p.hitboxCircle : null,
        })),
        deleteMissing: true,
      });
      setMsg(`Saved ${data.savedCount} hitboxes (${data.deletedCount} removed).`);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  async function publish() {
    try {
      await api.post(`/admin/levels/${id}/publish`);
      setMsg("Published.");
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  async function updateLevelField(patch) {
    try {
      const { data } = await api.patch(`/admin/levels/${id}`, patch);
      setLevel(data.level);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail || e.message));
    }
  }

  // ---------- Keyboard shortcuts (Ctrl+S, Ctrl+Z, arrow-key nudge) ----------
  useEffect(() => {
    const onKey = (e) => {
      const cmd = e.ctrlKey || e.metaKey;
      // Ignore keydowns coming from form inputs — the sidebar has many.
      const tag = (e.target?.tagName || "").toLowerCase();
      const inField = ["input", "textarea", "select"].includes(tag);

      if (cmd && (e.key === "s" || e.key === "S")) {
        e.preventDefault();
        if (!saving) save();
        return;
      }
      if (cmd && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        const stack = undoRef.current;
        if (stack.length) {
          const last = stack.pop();
          setObjects(last);
        }
        return;
      }
      if (inField) return;
      // Arrow-key vertex nudge
      const step = e.shiftKey ? NUDGE_FINE : NUDGE_COARSE;
      const dir = { ArrowUp: [0, -step], ArrowDown: [0, step], ArrowLeft: [-step, 0], ArrowRight: [step, 0] }[e.key];
      if (!dir) return;
      if (selectedIdx < 0 || !selected || selected.hitboxShape !== "polygon" || selectedVertex < 0) return;
      e.preventDefault();
      mutate((prev) => prev.map((o, i) => {
        if (i !== selectedIdx) return o;
        const nextPoly = o.hitboxPolygon.map((p, k) => k === selectedVertex
          ? [clamp(round2(p[0] + dir[0]), 0, 100), clamp(round2(p[1] + dir[1]), 0, 100)]
          : p);
        return {
          ...o,
          hitboxPolygon: nextPoly,
          ...(polygonBbox(nextPoly) || {}),
        };
      }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, selectedIdx, selectedVertex, saving, mutate]);

  if (!level) return <p>Loading editor…</p>;

  const bg = level.sceneImageUrl ? sceneUrl(level.sceneImageUrl) : null;
  const canvasClass = [
    "editor-canvas",
    previewMode && "editor-canvas--preview",
    testTapMode && "editor-canvas--test-tap",
  ].filter(Boolean).join(" ");

  return (
    <div data-testid="level-editor">
      <div className="admin-header">
        <div>
          <h1>{level.name}</h1>
          <p className="admin-header__sub">
            <code>{level.slug}</code> · {objects.length} hitboxes · {level.contentStatus}
          </p>
        </div>
        <div className="admin-actions">
          <BigButton variant="ghost" onClick={() => navigate("/admin/levels")} data-testid="editor-back">← Back</BigButton>
          <BigButton
            variant={previewMode ? "coral" : "ghost"}
            onClick={() => setPreviewMode((p) => !p)}
            data-testid="editor-preview-toggle"
          >
            {previewMode ? "Preview: ON" : "Preview"}
          </BigButton>
          <BigButton
            variant={testTapMode ? "coral" : "ghost"}
            onClick={() => { setTestTapMode((t) => !t); setTestTapResult(null); }}
            data-testid="editor-test-tap-toggle"
          >
            {testTapMode ? "Test tap: ON" : "Test tap"}
          </BigButton>
          <BigButton onClick={save} disabled={saving} data-testid="editor-save">
            {saving ? "Saving…" : "Save"}
            <span className="editor-kbd-hint">⌘S</span>
          </BigButton>
          <BigButton variant="sun" onClick={publish} data-testid="editor-publish">Publish</BigButton>
        </div>
      </div>
      {err && <div className="admin-error">{err}</div>}
      {msg && <div className="admin-ok">{msg}</div>}
      {validation.length > 0 && (
        <div className="admin-warning" data-testid="editor-warnings">
          {validation.map((v, i) => <div key={i}>⚠ {v}</div>)}
        </div>
      )}

      <div className="editor-shell">
        <div className="editor-canvas-wrap">
          <div
            ref={canvasRef}
            className={canvasClass}
            style={{ backgroundImage: bg ? `url(${bg})` : "none" }}
            data-testid="editor-canvas"
            onClick={onCanvasClick}
          >
            {/* Bounding rectangles — always drawn as dashed guide, click-to-select. */}
            {objects.map((o, i) => {
              const isSelected = i === selectedIdx;
              const { stroke } = paletteFor(i);
              return (
                <div
                  key={o.id || `new-${i}`}
                  className={`editor-box ${isSelected ? "selected" : ""} editor-box--ghost`}
                  data-testid={`editor-box-${i}`}
                  style={{
                    left: `${o.xPercent}%`, top: `${o.yPercent}%`,
                    width: `${o.widthPercent}%`, height: `${o.heightPercent}%`,
                    borderColor: isSelected ? stroke : "transparent",
                    pointerEvents: previewMode || testTapMode ? "none" : "auto",
                  }}
                  onPointerDown={(e) => {
                    if (previewMode || testTapMode) return;
                    e.stopPropagation();
                    setSelectedIdx(i);
                    setSelectedVertex(-1);
                  }}
                >
                  {isSelected && (
                    <span
                      className="editor-box__label"
                      style={{ background: stroke, color: "#fff" }}
                    >
                      {i + 1}. {o.learningObject?.displayName || "New"}
                    </span>
                  )}
                </div>
              );
            })}

            {/* Shape overlay — per-object colours, dim inactive, active pops. */}
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="editor-shape-svg"
              data-testid="editor-shape-svg"
            >
              {objects.map((o, i) => {
                const isSelected = i === selectedIdx;
                const { fill, stroke } = paletteFor(i);
                const cls = isSelected ? "editor-shape-poly--active" : "editor-shape-poly--dim";
                const sw = isSelected ? 0.5 : 0.3;
                if (o.hitboxShape === "circle" && o.hitboxCircle) {
                  return (
                    <circle
                      key={o.id || `svg-${i}`}
                      cx={o.hitboxCircle.cx} cy={o.hitboxCircle.cy} r={o.hitboxCircle.radius}
                      fill={fill} stroke={stroke} strokeWidth={sw}
                      className={cls}
                      data-testid={`editor-shape-${i}`}
                    />
                  );
                }
                if (o.hitboxShape === "polygon" && (o.hitboxPolygon || []).length >= 3) {
                  const pts = o.hitboxPolygon.map((p) => `${p[0]},${p[1]}`).join(" ");
                  return (
                    <polygon
                      key={o.id || `svg-${i}`}
                      points={pts}
                      fill={fill} stroke={stroke} strokeWidth={sw}
                      className={cls}
                      data-testid={`editor-shape-${i}`}
                    />
                  );
                }
                // rectangle
                return (
                  <rect
                    key={o.id || `svg-${i}`}
                    x={o.xPercent} y={o.yPercent}
                    width={o.widthPercent} height={o.heightPercent}
                    fill={fill} stroke={stroke} strokeWidth={sw}
                    className={cls}
                    data-testid={`editor-shape-${i}`}
                  />
                );
              })}
            </svg>

            {/* Interactive handles for the selected shape. */}
            {!previewMode && !testTapMode && selected && selected.hitboxShape === "circle" && selected.hitboxCircle && (
              <>
                <div
                  className="editor-vertex editor-vertex--center"
                  data-testid="editor-circle-center"
                  style={{ left: `${selected.hitboxCircle.cx}%`, top: `${selected.hitboxCircle.cy}%` }}
                  onPointerDown={(e) => onCircleDown(e, selectedIdx, "circle-move")}
                />
                <div
                  className="editor-vertex editor-vertex--radius"
                  data-testid="editor-circle-radius"
                  style={{ left: `${selected.hitboxCircle.cx + selected.hitboxCircle.radius}%`, top: `${selected.hitboxCircle.cy}%` }}
                  onPointerDown={(e) => onCircleDown(e, selectedIdx, "circle-radius")}
                />
              </>
            )}
            {!previewMode && !testTapMode && selected && selected.hitboxShape === "polygon" && (selected.hitboxPolygon || []).map((p, vIdx) => (
              <div
                key={`v-${vIdx}`}
                className={`editor-vertex${selectedVertex === vIdx ? " editor-vertex--selected" : ""}`}
                data-testid={`editor-vertex-${vIdx}`}
                style={{ left: `${p[0]}%`, top: `${p[1]}%` }}
                onPointerDown={(e) => onVertexDown(e, selectedIdx, vIdx)}
                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); deleteVertex(selectedIdx, vIdx); }}
                title="Drag to move · Shift-drag snaps to 1% · Arrow keys nudge · Right-click deletes"
              />
            ))}

            {/* Test-tap result readout. */}
            {testTapMode && testTapResult && (
              <div
                className="editor-test-tap-result"
                data-testid="editor-test-tap-result"
                style={{ left: testTapResult.clientX, top: testTapResult.clientY }}
              >
                ({round1(testTapResult.xPct)}%, {round1(testTapResult.yPct)}%) → {testTapResult.names.length
                  ? testTapResult.names.join(", ")
                  : "no hit"}
              </div>
            )}
          </div>
        </div>

        <aside className="editor-side">
          <h3>Scene</h3>
          <label style={{ fontSize: "0.8rem", color: "var(--wmleye-ink-soft)" }}>
            Background URL
            <input
              value={level.sceneImageUrl || ""}
              onChange={(e) => setLevel({ ...level, sceneImageUrl: e.target.value })}
              onBlur={() => updateLevelField({ sceneImageUrl: level.sceneImageUrl })}
              style={{ width: "100%", padding: "0.35rem 0.5rem", border: "2px solid var(--wmleye-line)", borderRadius: 8 }}
              data-testid="editor-scene-url"
            />
          </label>

          <h3>Hitboxes ({objects.length})</h3>
          <div className="editor-object-list">
            {objects.map((o, i) => {
              const { stroke } = paletteFor(i);
              const thumb = o.learningObject?.thumbnailUrl;
              return (
                <button
                  key={o.id || `new-${i}`}
                  className={i === selectedIdx ? "selected" : ""}
                  onClick={() => { setSelectedIdx(i); setSelectedVertex(-1); }}
                  data-testid={`editor-object-${i}`}
                >
                  <span className="editor-shape-swatch" style={{ background: stroke }} />
                  {thumb ? (
                    <ThumbWithFallback
                      src={thumb}
                      alt=""
                      className="editor-object-thumb"
                      data-testid={`editor-object-${i}-thumb`}
                      placeholderLabel="?"
                    />
                  ) : null}
                  <span>
                    {i + 1}. {o.learningObject?.displayName || o.learningObjectId?.slice(0, 6) || "?"}
                    {o.isRequired ? "" : " (bonus)"}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="admin-actions">
            <BigButton variant="ghost" onClick={addObject} data-testid="editor-add">+ Add hitbox</BigButton>
            <BigButton variant="coral" onClick={removeSelected} disabled={!selected} data-testid="editor-remove">Remove</BigButton>
          </div>

          {selected ? (
            <>
              <h3>Selected object</h3>
              <label style={{ fontSize: "0.8rem", color: "var(--wmleye-ink-soft)" }}>
                Learning object
                <select
                  value={selected.learningObjectId}
                  onChange={(e) => {
                    const lo = learningObjects.find((x) => x.id === e.target.value);
                    patchObject(selectedIdx, { learningObjectId: e.target.value, learningObject: lo });
                  }}
                  style={{ width: "100%", padding: "0.4rem 0.5rem", border: "2px solid var(--wmleye-line)", borderRadius: 8 }}
                  data-testid="editor-lo-select"
                >
                  {learningObjects.map((lo) => (
                    <option key={lo.id} value={lo.id}>{lo.displayName} · {lo.category}</option>
                  ))}
                </select>
              </label>

              <LoRenameAndThumbnail
                key={selected.id || selectedIdx}
                levelObject={selected}
                sameLevelObjects={objects}
                onRenamed={(newLo) => {
                  // Extra guard — should never be nullish thanks to the
                  // defensive check inside LoRenameAndThumbnail, but keep
                  // the shim so a future response-shape drift can't crash
                  // the level editor mid-edit.
                  if (!newLo || !newLo.id) return;
                  patchObject(selectedIdx, { learningObjectId: newLo.id, learningObject: newLo });
                }}
                onRelinked={(newLevelObject) => {
                  // 409 clash → user chose "Reuse existing LO". Backend
                  // returns a fully-hydrated level_object (with nested
                  // .learningObject). Merge everything so hitbox, thumb
                  // URL, etc all reflect the swap.
                  if (!newLevelObject || !newLevelObject.learningObject?.id) return;
                  patchObject(selectedIdx, {
                    learningObjectId: newLevelObject.learningObject.id,
                    learningObject: newLevelObject.learningObject,
                    missionThumbnailUrl: newLevelObject.missionThumbnailUrl
                                          ?? selected.missionThumbnailUrl,
                  });
                }}
                onThumbUpdated={(url) => {
                  patchObject(selectedIdx, { missionThumbnailUrl: url });
                }}
              />

              <label style={{ fontSize: "0.8rem", color: "var(--wmleye-ink-soft)" }}>
                Hit shape
                <select
                  value={selected.hitboxShape || "polygon"}
                  onChange={(e) => switchShape(selectedIdx, e.target.value)}
                  style={{ width: "100%", padding: "0.4rem 0.5rem", border: "2px solid var(--wmleye-line)", borderRadius: 8 }}
                  data-testid="editor-shape-select"
                >
                  <option value="polygon">Polygon</option>
                  <option value="rectangle">Rectangle</option>
                  <option value="circle">Circle</option>
                </select>
              </label>

              <div className="editor-field-row">
                <label>X %<input type="number" step="0.5" value={selected.xPercent} onChange={(e) => patchObject(selectedIdx, { xPercent: numOr(e.target.value, 0) })} /></label>
                <label>Y %<input type="number" step="0.5" value={selected.yPercent} onChange={(e) => patchObject(selectedIdx, { yPercent: numOr(e.target.value, 0) })} /></label>
              </div>
              <div className="editor-field-row">
                <label>Width %<input type="number" step="0.5" value={selected.widthPercent} onChange={(e) => patchObject(selectedIdx, { widthPercent: numOr(e.target.value, 5) })} /></label>
                <label>Height %<input type="number" step="0.5" value={selected.heightPercent} onChange={(e) => patchObject(selectedIdx, { heightPercent: numOr(e.target.value, 5) })} /></label>
              </div>
              <div className="editor-field-row">
                <label>Padding %<input type="number" value={selected.hitboxPaddingPercent} onChange={(e) => patchObject(selectedIdx, { hitboxPaddingPercent: numOr(e.target.value, 0) })} /></label>
                <label>Token reward<input type="number" value={selected.tokenReward} onChange={(e) => patchObject(selectedIdx, { tokenReward: parseInt(e.target.value || "0") })} /></label>
              </div>

              {selected.hitboxShape === "polygon" && (
                <div className="editor-shape-panel" data-testid="editor-polygon-panel">
                  <h4>Polygon <span className="editor-shape-hint">· {selected.hitboxPolygon.length} vertices</span></h4>
                  <div className="admin-actions">
                    <BigButton variant="ghost" onClick={() => autoFit(selectedIdx)} data-testid="editor-poly-autofit">
                      Auto-fit
                    </BigButton>
                    <BigButton variant="ghost" onClick={() => resetToSeeded(selectedIdx)} data-testid="editor-poly-reset">
                      Reset
                    </BigButton>
                    <BigButton variant="ghost" onClick={() => simplifyPolygon(selectedIdx)} data-testid="editor-poly-simplify">
                      Simplify
                    </BigButton>
                    <BigButton
                      variant="ghost"
                      onClick={() => selectedVertex >= 0 && deleteVertex(selectedIdx, selectedVertex)}
                      disabled={selectedVertex < 0 || selected.hitboxPolygon.length <= 3}
                      data-testid="editor-poly-delete-vertex"
                    >
                      Delete vertex
                    </BigButton>
                  </div>
                  <p className="editor-shape-hint">
                    Click <b>polygon edge</b> to insert a vertex · drag vertex to move ·
                    right-click deletes · <b>Shift-drag</b> snaps to 1% grid ·
                    <b>arrows</b> nudge 0.5% (<b>Shift+arrows</b> = 0.1%) ·
                    <b>Ctrl/⌘+Z</b> undoes.
                  </p>
                  <VertexTable
                    polygon={selected.hitboxPolygon}
                    selectedVertex={selectedVertex}
                    onSelect={setSelectedVertex}
                    imgW={level.sceneWidthPx || 1264}
                    imgH={level.sceneHeightPx || 848}
                  />
                </div>
              )}

              {selected.hitboxShape === "circle" && selected.hitboxCircle && (
                <div className="editor-shape-panel" data-testid="editor-circle-panel">
                  <h4>Circle</h4>
                  <div className="editor-field-row">
                    <label>cx %<input type="number" step="0.5" value={selected.hitboxCircle.cx}
                      onChange={(e) => patchObject(selectedIdx, { hitboxCircle: { ...selected.hitboxCircle, cx: numOr(e.target.value, 0) } })} /></label>
                    <label>cy %<input type="number" step="0.5" value={selected.hitboxCircle.cy}
                      onChange={(e) => patchObject(selectedIdx, { hitboxCircle: { ...selected.hitboxCircle, cy: numOr(e.target.value, 0) } })} /></label>
                  </div>
                  <label>radius %<input type="number" step="0.5" value={selected.hitboxCircle.radius}
                    onChange={(e) => patchObject(selectedIdx, { hitboxCircle: { ...selected.hitboxCircle, radius: numOr(e.target.value, 1) } })} /></label>
                  <p className="editor-shape-hint">Drag the centre dot to move · drag the ring handle to resize.</p>
                </div>
              )}

              <label style={{ fontSize: "0.8rem", color: "var(--wmleye-ink-soft)" }}>
                Spoken hint
                <input value={selected.hintText || ""} onChange={(e) => patchObject(selectedIdx, { hintText: e.target.value })} data-testid="editor-hint-text" />
              </label>
              <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", marginTop: "0.35rem" }}>
                <label style={{ display: "flex", flexDirection: "row", gap: "0.25rem", alignItems: "center" }}>
                  <input type="checkbox" checked={selected.isRequired} onChange={(e) => patchObject(selectedIdx, { isRequired: e.target.checked })} data-testid="editor-required" /> Required
                </label>
                <label style={{ display: "flex", flexDirection: "row", gap: "0.25rem", alignItems: "center" }}>
                  <input type="checkbox" checked={selected.isBonusObject} onChange={(e) => patchObject(selectedIdx, { isBonusObject: e.target.checked })} data-testid="editor-bonus" /> Bonus
                </label>
              </div>
            </>
          ) : (
            <p style={{ color: "var(--wmleye-ink-soft)" }}>Click a hitbox on the canvas or list.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

/** Sidebar vertex table — both px and % readings so the admin can spot-check
 *  the pixel location against the raw scene coord system. */
function VertexTable({ polygon, selectedVertex, onSelect, imgW, imgH }) {
  if (!polygon || polygon.length === 0) return null;
  return (
    <table className="editor-vertex-table" data-testid="editor-vertex-table">
      <thead>
        <tr>
          <th>#</th>
          <th>x %</th><th>y %</th>
          <th>x px</th><th>y px</th>
        </tr>
      </thead>
      <tbody>
        {polygon.map((p, i) => (
          <tr
            key={i}
            className={i === selectedVertex ? "selected" : ""}
            onClick={() => onSelect(i)}
            data-testid={`editor-vertex-row-${i}`}
          >
            <td>{i}</td>
            <td>{round2(p[0]).toFixed(2)}</td>
            <td>{round2(p[1]).toFixed(2)}</td>
            <td>{Math.round((p[0] / 100) * imgW)}</td>
            <td>{Math.round((p[1] / 100) * imgH)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────
function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }
function numOr(v, fallback) { const n = parseFloat(v); return Number.isFinite(n) ? n : fallback; }
function round1(n) { return Math.round(n * 10) / 10; }
function round2(n) { return Math.round(n * 100) / 100; }

function removeNearDuplicates(poly, threshold = 1.0) {
  if (!poly || poly.length <= 3) return poly;
  const out = [];
  for (const [x, y] of poly) {
    const prev = out[out.length - 1];
    if (prev && Math.hypot(prev[0] - x, prev[1] - y) < threshold) continue;
    out.push([round1(x), round1(y)]);
  }
  if (out.length > 3) {
    const first = out[0], last = out[out.length - 1];
    if (Math.hypot(first[0] - last[0], first[1] - last[1]) < threshold) out.pop();
  }
  return out.length >= 3 ? out : poly;
}

function sceneUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  if (u.startsWith("/uploads/")) return `${process.env.REACT_APP_BACKEND_URL}/api${u}`;
  return u;
}

/** Point-to-segment distance for edge-click detection. Returns the nearest
 *  point on the segment as well so we can insert a new vertex exactly there. */
function distPointToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy || 1e-9;
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = ax + t * dx, qy = ay + t * dy;
  return { dist: Math.hypot(px - qx, py - qy), pt: [qx, qy] };
}

/** Client-side shape-aware hit test used by the Test-tap mode. Mirrors the
 *  backend/frontend `isPointInHitbox` for polygons and circles; rectangles
 *  fall back to a bbox check. Padding is applied for parity with the server. */
function pointInHitbox(x, y, o) {
  const pad = o.hitboxPaddingPercent ?? 0;
  const shape = o.hitboxShape || "rectangle";
  if (shape === "circle" && o.hitboxCircle) {
    const r = o.hitboxCircle.radius * (1 + pad / 100);
    return Math.hypot(x - o.hitboxCircle.cx, y - o.hitboxCircle.cy) <= r;
  }
  if (shape === "polygon" && o.hitboxPolygon?.length >= 3) {
    const poly = o.hitboxPolygon;
    const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
    const cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
    const scale = 1 + pad / 100;
    const padded = poly.map(([px, py]) => [cx + (px - cx) * scale, cy + (py - cy) * scale]);
    let inside = false;
    for (let i = 0, j = padded.length - 1; i < padded.length; j = i++) {
      const [xi, yi] = padded[i], [xj, yj] = padded[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-9) + xi) inside = !inside;
    }
    return inside;
  }
  const padX = (o.widthPercent * pad) / 100;
  const padY = (o.heightPercent * pad) / 100;
  return x >= o.xPercent - padX && x <= o.xPercent + o.widthPercent + padX
    && y >= o.yPercent - padY && y <= o.yPercent + o.heightPercent + padY;
}

function validate(level, objs) {
  const warnings = [];
  const need = level?.difficulty === "hard" ? 12 : 10;
  const required = objs.filter((o) => o.isRequired);
  if (required.length < need) {
    warnings.push(`Only ${required.length} required objects (need at least ${need} for ${level?.difficulty || "easy"} mode).`);
  }
  for (let i = 0; i < objs.length; i++) {
    for (let j = i + 1; j < objs.length; j++) {
      const a = objs[i], b = objs[j];
      const overlap =
        a.xPercent < b.xPercent + b.widthPercent &&
        a.xPercent + a.widthPercent > b.xPercent &&
        a.yPercent < b.yPercent + b.heightPercent &&
        a.yPercent + a.heightPercent > b.yPercent;
      if (overlap) { warnings.push(`Hitbox #${i + 1} overlaps #${j + 1}.`); break; }
    }
  }
  const missingHint = objs.filter((o) => !o.hintText || !o.hintText.trim()).length;
  if (missingHint > 0) warnings.push(`${missingHint} hitbox(es) missing spoken hint text.`);
  for (let i = 0; i < objs.length; i++) {
    const o = objs[i];
    if (o.hitboxShape === "polygon" && (!o.hitboxPolygon || o.hitboxPolygon.length < 3)) {
      warnings.push(`Hitbox #${i + 1} is polygon but has fewer than 3 vertices.`);
    }
    if (o.hitboxShape === "circle" && (!o.hitboxCircle || !(o.hitboxCircle.radius > 0))) {
      warnings.push(`Hitbox #${i + 1} is circle but radius is not set.`);
    }
  }
  return warnings;
}

// -----------------------------------------------------------------------------
// LoRenameAndThumbnail
//
// Rename the selected level_object's LearningObject (with fork discipline —
// see backend admin_rename_level_object) and edit its mission-card thumbnail
// (re-crop from scene OR upload custom PNG/JPEG). Everything is scoped to the
// SINGLE selected level_object; other LOs and other levels are untouched.
// -----------------------------------------------------------------------------
function LoRenameAndThumbnail({ levelObject, sameLevelObjects, onRenamed, onRelinked, onThumbUpdated }) {
  const lo = levelObject.learningObject || {};
  const [display, setDisplay] = React.useState(lo.displayName || "");
  const [spelling, setSpelling] = React.useState((lo.spellingSequence || []).join(""));
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [thumbUrl, setThumbUrl] = React.useState(levelObject.missionThumbnailUrl || "");
  const [thumbBust, setThumbBust] = React.useState(0);
  // Reuse-existing-LO modal state, populated when the rename returns a
  // 409 canonical_name_clash for an LO ALREADY IN USE on this level.
  const [reuseOffer, setReuseOffer] = React.useState(null);
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    setDisplay(lo.displayName || "");
    setSpelling((lo.spellingSequence || []).join(""));
    setThumbUrl(levelObject.missionThumbnailUrl || "");
    setThumbBust(0);
  }, [levelObject.id, lo.displayName, levelObject.missionThumbnailUrl, lo.spellingSequence]);

  async function doRename() {
    if (!display.trim() || display.trim() === lo.displayName) return;
    // Guard against the "Add hitbox → immediately Rename before Save" flow.
    // Fresh hitboxes have no server id yet, so the URL would become
    // /api/admin/level-objects/undefined/rename → 404. Surface a helpful
    // message instead of firing a doomed request.
    if (!levelObject.id) {
      setMsg(
        "This hitbox hasn’t been saved yet — click Save at the top of the level editor first, then you can rename it.",
      );
      return;
    }
    setBusy(true); setMsg(null);
    try {
      const body = { displayName: display.trim() };
      if (spelling && spelling.trim()) {
        // Empty string in the ribbon means the space separator between words
        body.spellingSequence = spelling.split("").map((c) => (c === " " ? " " : c.toUpperCase()));
      }
      const { data } = await api.post(
        `/admin/level-objects/${levelObject.id}/rename`, body,
      );
      // Defensive: backend's no-op path used to return without a
      // `learningObject` field, which crashed `onRenamed(data.learningObject).id`
      // when the frontend forwarded `undefined` up to `patchObject`. Backend
      // is fixed to always include it, but we still guard here in case an
      // older backend build responds.
      const newLo = data?.learningObject;
      if (!newLo || !newLo.id) {
        setMsg(data?.noop ? "No changes — nothing to rename." : "Rename returned an unexpected shape — please refresh the page.");
        return;
      }
      setMsg(
        data.noop ? "No changes — nothing to rename."
                  : data.forked ? "Forked new LO ✓"
                                : "Renamed in place ✓",
      );
      if (!data.noop) onRenamed?.(newLo);
      setThumbBust((n) => n + 1); // mission thumb regenerated server-side
    } catch (exc) {
      const detail = exc?.response?.data?.detail;
      // Friendly 409 message — the batch color-rename fix (Iteration 29)
      // stopped silently swallowing canonical clashes and now returns 409
      // with { error: "canonical_name_clash", existingLoId, message }.
      if (exc?.response?.status === 409 && detail?.error === "canonical_name_clash") {
        // Is the clashing LO ALREADY IN USE on THIS level? If so, offer
        // to reuse it (swap this level_object's learningObjectId to
        // point at the existing LO) rather than force the user to pick
        // a different name.
        const existingLoId = detail.existingLoId;
        const sameLevelHit = (sameLevelObjects || []).find(
          (o) => o.learningObjectId === existingLoId && o.id !== levelObject.id,
        );
        const existingLo = sameLevelHit?.learningObject || null;
        if (sameLevelHit && existingLo) {
          setReuseOffer({
            existingLoId,
            existingDisplay: existingLo.displayName || detail.requestedCanonical,
            existingCanonical: existingLo.canonicalName || detail.requestedCanonical,
            existingSlotId: sameLevelHit.id,
          });
          setMsg(null);
        } else {
          // Clash is on a DIFFERENT level — just show the friendly toast.
          setReuseOffer(null);
          setMsg(
            `“${display.trim()}” is already used by another object` +
            (detail.requestedCanonical ? ` (canonical: ${detail.requestedCanonical})` : "") +
            `. Pick a different name.`,
          );
        }
      } else {
        setReuseOffer(null);
        setMsg(`Rename failed: ${formatApiErrorDetail(detail) || exc?.message || "unknown error"}`);
      }
    } finally {
      setBusy(false);
    }
  }

  async function doReuseExistingLo() {
    if (!reuseOffer) return;
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(
        `/admin/level-objects/${levelObject.id}/relink-lo`,
        { learningObjectId: reuseOffer.existingLoId },
      );
      const newLevelObject = data?.levelObject;
      if (!newLevelObject || !newLevelObject.learningObject?.id) {
        setMsg("Reuse returned an unexpected shape — please refresh the page.");
        return;
      }
      setMsg(`Now reusing existing “${reuseOffer.existingDisplay}” ✓`);
      onRelinked?.(newLevelObject);
      setReuseOffer(null);
      setThumbBust((n) => n + 1);
    } catch (exc) {
      const detail = exc?.response?.data?.detail;
      setMsg(`Reuse failed: ${formatApiErrorDetail(detail) || exc?.message || "unknown error"}`);
    } finally {
      setBusy(false);
    }
  }

  async function doRecrop() {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post(
        `/admin/level-objects/${levelObject.id}/recrop-thumbnail`,
      );
      setThumbUrl(data.url);
      setThumbBust((n) => n + 1);
      onThumbUpdated?.(data.url);
      setMsg("Re-cropped from scene ✓");
    } catch (exc) {
      // Backend now returns structured detail: {error, reason, message}.
      // Prefer the friendly `message`; fall back to `reason` then generic.
      const detail = exc?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object") {
        msg = detail.message || detail.reason || detail.error;
      } else {
        msg = formatApiErrorDetail(detail) || exc?.message;
      }
      setMsg(`Recrop failed: ${msg || "unknown error"}`);
    } finally {
      setBusy(false);
    }
  }

  async function doUpload(file) {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setMsg("File too large (max 5 MB)"); return;
    }
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/level-objects/${levelObject.id}/upload-thumbnail`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setThumbUrl(data.missionThumbnailUrl);
      setThumbBust((n) => n + 1);
      onThumbUpdated?.(data.missionThumbnailUrl);
      setMsg("Custom thumbnail uploaded ✓");
    } catch (exc) {
      setMsg(`Upload failed: ${formatApiErrorDetail(exc)}`);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const previewSrc = thumbUrl ? `${thumbUrl}${thumbBust ? `?v=${thumbBust}` : ""}` : "";
  const lbl = { fontSize: "0.8rem", color: "var(--wmleye-ink-soft)" };
  const inp = { width: "100%", padding: "0.4rem 0.5rem",
                border: "2px solid var(--wmleye-line)", borderRadius: 8 };

  return (
    <div style={{ border: "2px dashed var(--wmleye-line)", borderRadius: 12,
                  padding: "0.75rem", marginTop: "0.75rem" }}
         data-testid="editor-lo-rename-thumb">
      <div style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: "0.5rem" }}>
        Rename target &amp; mission thumbnail
      </div>

      <label style={lbl}>
        Display name
        <input style={inp} value={display}
               onChange={(e) => setDisplay(e.target.value)}
               data-testid="editor-lo-display-input"
               maxLength={60} />
      </label>
      <label style={{ ...lbl, marginTop: "0.4rem" }}>
        Spelling (letters + spaces)
        <input style={inp} value={spelling}
               onChange={(e) => setSpelling(e.target.value)}
               data-testid="editor-lo-spelling-input"
               placeholder="e.g. BLUE DRUM (letters only, space for word break)" />
      </label>
      <BigButton variant="mint" onClick={doRename} disabled={busy || !display.trim()}
                 data-testid="editor-lo-rename-btn">
        Save rename (fork if shared)
      </BigButton>

      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem",
                    marginTop: "0.75rem", padding: "0.5rem",
                    background: "var(--wmleye-surface-soft, #f8f6f2)", borderRadius: 8 }}>
        <div style={{ flex: "0 0 128px" }}>
          <div style={{ fontSize: "0.7rem", color: "var(--wmleye-ink-soft)", marginBottom: 4 }}>
            Mission thumbnail
          </div>
          {previewSrc ? (
            <ThumbWithFallback
              src={previewSrc}
              alt="mission thumbnail"
              style={{ width: 128, height: 128, objectFit: "cover",
                       borderRadius: 8, border: "1px solid var(--wmleye-line)" }}
              data-testid="editor-mission-thumb-preview"
              placeholderLabel="image unavailable — try Re-crop from scene"
            />
          ) : (
            <div style={{ width: 128, height: 128, borderRadius: 8,
                          border: "1px dashed var(--wmleye-line)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "0.7rem", color: "var(--wmleye-ink-soft)" }}>
              none
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", flex: 1 }}>
          <BigButton variant="ghost" onClick={doRecrop} disabled={busy}
                     data-testid="editor-mission-thumb-recrop">
            Re-crop from scene
          </BigButton>
          <input ref={fileInputRef} type="file"
                 accept="image/png,image/jpeg"
                 onChange={(e) => doUpload(e.target.files?.[0])}
                 disabled={busy}
                 data-testid="editor-mission-thumb-upload"
                 style={{ fontSize: "0.75rem" }} />
        </div>
      </div>

      {reuseOffer ? (
        <div
          role="dialog"
          aria-labelledby="reuse-lo-title"
          data-testid="editor-lo-reuse-modal"
          style={{
            marginTop: "0.6rem",
            padding: "0.75rem 0.9rem",
            border: "2px solid var(--wmleye-line)",
            borderRadius: 12,
            background: "#fff4e0",
          }}
        >
          <div
            id="reuse-lo-title"
            style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: "0.35rem" }}
          >
            “{reuseOffer.existingDisplay}” already exists on this level
          </div>
          <div style={{ fontSize: "0.8rem", color: "var(--wmleye-ink)", marginBottom: "0.6rem" }}>
            Another slot on this level is already using{" "}
            <code>{reuseOffer.existingCanonical}</code>. You can point this
            slot at the same LearningObject (keeps audio, spelling and
            thumbnail in sync across slots) or pick a different name.
          </div>
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
            <BigButton
              variant="mint"
              onClick={doReuseExistingLo}
              disabled={busy}
              data-testid="editor-lo-reuse-confirm"
            >
              Reuse existing LO
            </BigButton>
            <BigButton
              variant="ghost"
              onClick={() => { setReuseOffer(null); setMsg(null); }}
              disabled={busy}
              data-testid="editor-lo-reuse-cancel"
            >
              Pick a different name
            </BigButton>
          </div>
        </div>
      ) : null}

      {msg ? (
        <div style={{ marginTop: "0.5rem", fontSize: "0.75rem",
                      color: msg.includes("failed") ? "var(--wmleye-warn, #c33)" : "var(--wmleye-ink-soft)" }}
             data-testid="editor-lo-rename-msg">
          {msg}
        </div>
      ) : null}
    </div>
  );
}
